import * as React from "react";

/**
 * Linien-Icons der neuen Vorgangsansicht — dieselbe Machart wie
 * `redesign/kvpIcons.tsx` (1,7 px Strich, currentColor, 20er-Viewbox), aber
 * auf die hier gebrauchten Motive beschränkt.
 */

const Svg: React.FC<{ size?: number; children: React.ReactNode }> = ({ size = 16, children }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        {children}
    </svg>
);

export const IcoMail: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
        <path d="m3 6 7 5 7-5" />
    </Svg>
);

/**
 * Sprechblase — „Kommentieren".
 *
 * Das Repo hat diese Aktion schon zweimal so beschriftet (`NoteChat`,
 * `NotizZeile`, je `Comment16Regular` aus Fluent). v3 importiert nirgends
 * `@fluentui/react-icons`, deshalb hier als eigenes Motiv statt eines neuen
 * Fremd-Imports — und bewusst NICHT der Rücksprung-Pfeil `IcoAntwort`: der
 * meint „antworten", und genau dieses Wort soll verschwinden.
 */
export const IcoSprechblase: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M17 11.2a2.3 2.3 0 0 1-2.3 2.3H7.6L4 16.5v-3h-.7a2.3 2.3 0 0 1-2.3-2.3V5.8A2.3 2.3 0 0 1 3.3 3.5h11.4A2.3 2.3 0 0 1 17 5.8z" />
    </Svg>
);

/** Notizblock MIT Stift — eine Notiz schreibt man, ein Dokument liegt nur da. */
export const IcoNotiz: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M15 9.5v6.2a1.3 1.3 0 0 1-1.3 1.3H4.3A1.3 1.3 0 0 1 3 15.7V4.3A1.3 1.3 0 0 1 4.3 3h6.2" />
        <path d="M6.3 7.5h4M6.3 10.5h2.6" />
        <path d="m13.4 3.1 3.5 3.5-4.9 4.9-3.9.4.4-3.9z" />
    </Svg>
);

export const IcoAnruf: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M6.5 3.2 8 6.3 6.4 7.8a9.5 9.5 0 0 0 5.3 5.3l1.5-1.6 3.1 1.5v3a1.4 1.4 0 0 1-1.5 1.4C7.9 16.9 3.1 12.1 2.6 5.2A1.4 1.4 0 0 1 4 3.7Z" />
    </Svg>
);

export const IcoDokument: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M5 2.8h6.5L16 7.3v9.9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V3.8a1 1 0 0 1 1-1Z" />
        <path d="M11 2.8v5h5" />
    </Svg>
);

/** Kalenderblatt — der Termin als Ereignis im Notizstrom (PO 14.09.2026). */
export const IcoKalender: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <rect x="3" y="4.5" width="14" height="12.5" rx="1.8" />
        <path d="M3 8.5h14M7 2.8v3.2M13 2.8v3.2" />
        <path d="M6.5 11.5h2M11.5 11.5h2M6.5 14.2h2" />
    </Svg>
);

export const IcoAnhang: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M14.5 9.5 9.2 14.8a3.4 3.4 0 0 1-4.8-4.8l6-6a2.3 2.3 0 0 1 3.2 3.2l-6 6a1.1 1.1 0 0 1-1.6-1.6l5.3-5.3" />
    </Svg>
);

export const IcoChevron: React.FC<{ size?: number }> = ({ size = 13 }) => (
    <Svg size={size}>
        <path d="M7.5 4 13 10l-5.5 6" />
    </Svg>
);

/**
 * Derselbe Winkel, aber nach UNTEN — der Aufklapper eines Blocks, der sich
 * nach unten öffnet (Mockup `#i-chev`, V4-Sanierung 13.09.2026).
 *
 * Ein EIGENES Icon neben `IcoChevron` und keine Drehung von diesem: an
 * `IcoChevron` hängen V3 bis V6, `VerlaufAbschnitt` und `VerlaufEintraege`.
 * Ihn zentral zu drehen drehte jeden Aufklapper der Anwendung — und
 * aufgeklappt zeigte der gedrehte Rechtspfeil nach LINKS, was sich als
 * „zurück" liest statt als „zuklappen".
 */
export const IcoChevronUnten: React.FC<{ size?: number }> = ({ size = 13 }) => (
    <Svg size={size}>
        <path d="M4 7.5 10 13l6-5.5" />
    </Svg>
);

/**
 * Die Lupe des Suchfeldes (Mockup `#i-suche`, V4-Sanierung 13.09.2026).
 *
 * Neu hier und nicht aus `kvpIcons` geholt: dort ist die Lupe Teil der
 * Tabellen-Werkzeugleiste und trägt deren Strichstärke; das Suchfeld des
 * Archivs steht in der Vorgangsansicht und soll deren Machart haben.
 */
export const IcoLupe: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <circle cx="8.75" cy="8.75" r="5.75" />
        <path d="M13 13 17.5 17.5" />
    </Svg>
);

export const IcoCheck: React.FC<{ size?: number }> = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
            d="m4.5 10.5 3.5 3.5 7.5-8"
            stroke="currentColor"
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const IcoPlus: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M10 4.5v11M4.5 10h11" />
    </Svg>
);

/** Schließen/Abbrechen — seit PO 05.09.2026 steht im To-do-Kasten nur noch das ✕. */
export const IcoX: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="m5.5 5.5 9 9M14.5 5.5l-9 9" />
    </Svg>
);

export const IcoUpload: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M10 13.5V3.8M6.5 7 10 3.5 13.5 7" />
        <path d="M3.5 13v2.7a1.3 1.3 0 0 0 1.3 1.3h10.4a1.3 1.3 0 0 0 1.3-1.3V13" />
    </Svg>
);

export const IcoPosteingang: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M3 11h3.5l1.2 2h4.6l1.2-2H17" />
        <path d="M4.4 4h11.2l1.4 7v4.2a1.3 1.3 0 0 1-1.3 1.3H4.3A1.3 1.3 0 0 1 3 15.2V11Z" />
    </Svg>
);

export const IcoDownload: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M10 3.5v9M6.5 9 10 12.5 13.5 9" />
        <path d="M3.5 14v1.7a1.3 1.3 0 0 0 1.3 1.3h10.4a1.3 1.3 0 0 0 1.3-1.3V14" />
    </Svg>
);

export const IcoStift: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="m13.4 2.6 3.9 3.9L7.6 16.2l-4.6.9.9-4.6z" />
        <path d="m11.6 4.4 3.9 3.9" />
    </Svg>
);

export const IcoPapierkorb: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M3.5 5.3h13M8 5.3V3.8a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5" />
        <path d="M5.2 5.3 6 16.2a1.2 1.2 0 0 0 1.2 1.1h5.6a1.2 1.2 0 0 0 1.2-1.1l.8-10.9" />
        <path d="M8.4 8.5v5.6M11.6 8.5v5.6" />
    </Svg>
);

export const IcoAntwort: React.FC<{ size?: number }> = ({ size }) => (
    <Svg size={size}>
        <path d="M7.5 4.5 3 9l4.5 4.5" />
        <path d="M3 9h7.5a5.5 5.5 0 0 1 5.5 5.5v1" />
    </Svg>
);

/** Drei Punkte — „weitere Optionen". */
export const IcoMehr: React.FC<{ size?: number }> = ({ size }) => (
    <svg width={size ?? 16} height={size ?? 16} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        <circle cx="10" cy="4.5" r="1.6" />
        <circle cx="10" cy="10" r="1.6" />
        <circle cx="10" cy="15.5" r="1.6" />
    </svg>
);

/**
 * Dieselben drei Punkte WAAGERECHT (Mockup `#i-mehr`, V4-Sanierung
 * 13.09.2026). In einer waagerechten Knopfreihe steht ein hochkantes „⋮"
 * quer zur Leserichtung und wirkt wie ein Trennstrich zwischen den Knöpfen.
 *
 * Wieder ein eigenes Icon neben `IcoMehr` statt einer Drehung: das hochkante
 * trägt V3 bis V6 und die Verlaufszeilen, wo es in SENKRECHTEN Listen steht
 * und dort richtig ist.
 */
export const IcoMehrWaagerecht: React.FC<{ size?: number }> = ({ size }) => (
    <svg width={size ?? 16} height={size ?? 16} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        <circle cx="4.5" cy="10" r="1.6" />
        <circle cx="10" cy="10" r="1.6" />
        <circle cx="15.5" cy="10" r="1.6" />
    </svg>
);
